import { Injectable } from "@angular/core";
import moment from "moment";

import { IBroadcastsRepository, ELiveBroadcastingAction } from "app/js/src/domain/broadcasts/i-broadcasts-repository";
import { BroadcastPreset } from "app/js/src/domain/broadcasts/broadcast-preset";
import { SwitcherItem } from "app/js/src/domain/broadcasts/switcher-item";
import { ESwitcherAssetType } from "app/js/src/domain/broadcasts/switcher-item.type.enum";
import { BroadcastPresetAudioChannel } from "app/js/src/domain/broadcasts/broadcast-preset-audio-channel";
import { ICommonUtilInterface } from "app/js/src/domain/common/common-util.interface";
import { ShowRepository } from "app/js/src/domain/show-repository";
import { UserService } from "app/js/src/domain/user-service";
import { Socket } from "app/js/src/infrastructure/socket.service";

export interface CreateLiveEventBroadcastFlowParams {
  /** Event title; used as the broadcast preset name when provided. */
  title?: string;
  mainLiveFeedId: string;
  backupLiveFeedId: string;
  scheduledStartTime: moment.Moment;
}

const PRESET_NAME_PREFIX = "Live Event";
const SCHEDULED_SHOW_DURATION_MS = 60 * 60 * 1000; // 1 hour
const LIVE_FEED_DURATION_MS = 24 * 60 * 60 * 1000; // 24h placeholder

@Injectable()
export class CreateLiveEventBroadcastFlowService {
  constructor(
    private readonly broadcastsRepository: IBroadcastsRepository,
    private readonly showRepository: ShowRepository,
    private readonly commonUtils: ICommonUtilInterface,
    private readonly userService: UserService,
    private readonly socketService: Socket,
  ) {}

  async run(params: CreateLiveEventBroadcastFlowParams): Promise<string> {
    const preset = this.buildPreset(params);
    await this.broadcastsRepository.create(preset);

    await this.populatePresetAudioChannelsFromLiveFeeds(preset, params);
    await this.broadcastsRepository.update(preset);

    await this.showRepository.createBroadcastPresetShow(
      preset.id,
      params.scheduledStartTime,
      SCHEDULED_SHOW_DURATION_MS,
    );

    return preset.id;
  }

  private buildPreset(params: CreateLiveEventBroadcastFlowParams): BroadcastPreset {
    const name =
      params.title?.trim() ||
      `${PRESET_NAME_PREFIX} ${params.scheduledStartTime.format("YYYY-MM-DD HH:mm")}`;
    const preset = BroadcastPreset.createNew(name, this.commonUtils);
    const switcher = preset.switchers[0];

    const mainFeedItem = new SwitcherItem(
      this.commonUtils.generateGuid(),
      ESwitcherAssetType.LiveFeed,
      0,
      "Main",
      "",
      params.mainLiveFeedId,
      0,
      LIVE_FEED_DURATION_MS,
      false,
      true,  // isProgram: main feed on PROGRAM
      false,
    );
    const backupFeedItem = new SwitcherItem(
      this.commonUtils.generateGuid(),
      ESwitcherAssetType.LiveFeed,
      1,
      "Backup",
      "",
      params.backupLiveFeedId,
      0,
      LIVE_FEED_DURATION_MS,
      false,
      false,
      true,  // isPreview: backup feed on PREVIEW
    );

    switcher.items[0] = mainFeedItem;
    switcher.items[1] = backupFeedItem;

    return preset;
  }

  private async populatePresetAudioChannelsFromLiveFeeds(
    preset: BroadcastPreset,
    params: CreateLiveEventBroadcastFlowParams,
  ): Promise<void> {
    const channel = await this.userService.getCurrentChannel();
    if (!channel || !channel._availabilityZoneIds || channel._availabilityZoneIds.length === 0) {
      return;
    }

    const feedConfigs: { feedId: string; assetName: string }[] = [
      { feedId: params.mainLiveFeedId, assetName: "Main" },
      { feedId: params.backupLiveFeedId, assetName: "Backup" },
    ];

    for (const { feedId, assetName } of feedConfigs) {
      const response = await this.getLiveFeedAudioChannels(
        channel.uid,
        feedId,
        channel._availabilityZoneIds,
      );
      if (response && typeof response === "string") {
        try {
          const liveFeedAudioChannels = JSON.parse(response);
          for (let i = 0; i < liveFeedAudioChannels.length; i++) {
            const liveFeedAudioChannel = liveFeedAudioChannels[i];
            if (liveFeedAudioChannel.FrameRate) {
              continue;
            }
            const newAudioChannel = BroadcastPresetAudioChannel.createNew(
              ESwitcherAssetType.LiveFeed,
              `${assetName}_PID-${liveFeedAudioChannel.PID}`,
              feedId,
              liveFeedAudioChannel.LanguageCode ?? "XX",
              liveFeedAudioChannel.PID ?? 1,
              1.0,
            );
            newAudioChannel.isClearedForAir = true;
            preset.audioChannels.push(newAudioChannel);
          }
        } catch (error) {
          console.error("CreateLiveEventBroadcastFlow: Error parsing live feed audio channels:", error);
        }
      }
    }
  }

  private getLiveFeedAudioChannels(
    channelUid: string,
    liveFeedUid: string,
    availabilityZoneIds: number[],
  ): Promise<string | null> {
    return new Promise((resolve, reject) => {
      this.socketService.liveBroadcastingHubConnection
        .invoke("SendLiveBroadcastAction", {
          ChannelUid: channelUid,
          ActionType: ELiveBroadcastingAction.GetLiveFeedAudioChannels,
          Payload: liveFeedUid,
          AvailabilityZoneIds: availabilityZoneIds,
        })
        .then((result: { result: string }) => {
          resolve(result ? result.result : null);
        })
        .catch((err) => {
          console.error("CreateLiveEventBroadcastFlow: GetLiveFeedAudioChannels failed:", err);
          reject(err);
        });
    });
  }
}
